import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "presetList", "presetRow", "membersInput", "assistantCheckbox",
    "costPanel", "costBreakdown", "subtotal", "setupFee", "totalFirstYear",
    "discountInput", "discountMessage"
  ]

  static values = {
    tiers: Object,
    membersWord: { type: String, default: "members" },
    emptyState: { type: String, default: "Select teams to calculate costs" },
    setupFees: Object,
    discountFirstYear: Number,
    invalidCodeMessage: { type: String, default: "Invalid discount code" }
  }

  connect() {
    this.discountPercent = 0
    this.recalculate()
  }

  toggle(event) {
    const checkbox = event.target
    const assistantKey = checkbox.dataset.assistantKey

    if (checkbox.checked && assistantKey) {
      const assistantCb = this.assistantCheckboxTargets.find(
        cb => cb.dataset.parentKey === checkbox.value ||
              cb.value === assistantKey
      )
      if (assistantCb && !assistantCb.checked) {
        assistantCb.checked = true
      }
    }

    this.recalculate()
  }

  updateMembers() {
    this.recalculate()
  }

  applyDiscount() {
    const code = this.discountInputTarget.value.trim().toUpperCase()

    const knownCodes = {
      "EARLYBIRD": { percent: 20, label: "Early bird korting" }
    }

    if (knownCodes[code]) {
      this.discountPercent = knownCodes[code].percent
      this.discountMessageTarget.classList.remove("hidden")
      this.discountMessageTarget.classList.remove("text-red-600")
      this.discountMessageTarget.classList.add("text-green-600")
      this.discountMessageTarget.textContent = `${knownCodes[code].label} toegepast: ${this.discountPercent}% op jaarkosten`
    } else if (code === "") {
      this.discountPercent = 0
      this.discountMessageTarget.classList.add("hidden")
    } else {
      this.discountPercent = 0
      this.discountMessageTarget.classList.remove("hidden")
      this.discountMessageTarget.classList.remove("text-green-600")
      this.discountMessageTarget.classList.add("text-red-600")
      this.discountMessageTarget.textContent = this.invalidCodeMessageValue
    }

    this.recalculate()
  }

  recalculate() {
    const tiers = this.tiersValue
    const setupFees = this.setupFeesValue
    let annualTotal = 0
    let setupTotal = 0
    let breakdownHtml = ""

    this.presetRowTargets.forEach(row => {
      const checkbox = row.querySelector('input[type="checkbox"]')
      const membersInput = row.querySelector('input[type="number"]')

      if (!checkbox || !checkbox.checked) return

      const members = parseInt(membersInput?.value) || 0
      if (members <= 0) return

      let tierKey, tierLabel, annualFee, setupFee
      if (members <= (tiers.small?.staff_limit || 12)) {
        tierKey = "small"
        tierLabel = tiers.small?.label || "Klein team"
        annualFee = tiers.small?.annual_fee || 1200
        setupFee = setupFees.small || 2500
      } else if (members <= (tiers.medium?.staff_limit || 35)) {
        tierKey = "medium"
        tierLabel = tiers.medium?.label || "Medium team"
        annualFee = tiers.medium?.annual_fee || 2000
        setupFee = setupFees.medium || 2500
      } else {
        tierKey = "enterprise"
        tierLabel = tiers.enterprise?.label || "Enterprise"
        annualFee = 0
        setupFee = 0
      }

      const presetName = row.querySelector("span.font-medium")?.textContent || "Team"

      if (tierKey === "enterprise") {
        breakdownHtml += `
          <div class="flex justify-between text-sm">
            <span>${presetName} (${members} leden)</span>
            <span class="text-amber-600 font-medium">Neem contact op</span>
          </div>`
      } else {
        annualTotal += annualFee
        setupTotal += setupFee
        breakdownHtml += `
          <div class="flex justify-between text-sm">
            <span>${presetName} (${members} ${this.membersWordValue} — ${tierLabel})</span>
            <span class="font-medium">€ ${annualFee.toLocaleString("nl-NL")}/jaar</span>
          </div>`
      }
    })

    if (this.hasCostBreakdownTarget) {
      this.costBreakdownTarget.innerHTML = breakdownHtml || `<div class="text-sm text-gray-400 italic">${this.emptyStateValue}</div>`
    }

    let discountedAnnual = annualTotal
    if (this.discountPercent > 0) {
      discountedAnnual = annualTotal * (1 - this.discountPercent / 100)
    }

    if (this.hasSubtotalTarget) {
      this.subtotalTarget.textContent = `€ ${discountedAnnual.toLocaleString("nl-NL", { minimumFractionDigits: 0 })}`
    }
    if (this.hasSetupFeeTarget) {
      this.setupFeeTarget.textContent = `€ ${setupTotal.toLocaleString("nl-NL", { minimumFractionDigits: 0 })}`
    }
    if (this.hasTotalFirstYearTarget) {
      this.totalFirstYearTarget.textContent = `€ ${(discountedAnnual + setupTotal).toLocaleString("nl-NL", { minimumFractionDigits: 0 })}`
    }
  }
}
